import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Icon from './Icon';
import { Link, useLocation } from 'react-router-dom';
import { pascalCase } from '../utility/functions';
import RoleProtected from '../RoleProtected';
import { buildAccountURL } from './LinkTo';

const Wrapper = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
    cursor: pointer;
    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    border-radius: 25rem;
    user-select: none;
    color: ${({ theme, color }) => theme[color] || theme.text};
    transition: background-color, transform, 0.3s;
    background-color: ${({ theme }) => theme.button.background};
    font-weight: bold;
    font-size: inherit;
    padding: 0.2em 1.5em;
    overflow: hidden;
    min-height: 1.5em;

    &:hover {
        background-color: ${({ theme }) => theme.button.hover};
        border-color: ${({ theme }) => theme.button.hover};
        color: ${({ theme }) => theme.button.hoverText};
        // transform: translateY(-1px);
        // box-shadow: 0px 2px 2px ${({ theme }) => theme.shadow};
    }
    &:active {
        background-color: ${({ theme }) => theme.button.active};
        color: ${({ theme }) => theme.button.activeText};
        box-shadow: unset;
    }
    &:disabled {
        color: ${({ theme }) => theme.button.disabledText};
        background-color: ${({ theme }) => theme.button.disabled};
        border-color: unset;
        transform: unset;
        box-shadow: unset;
        cursor: default;
    }
    &:focus {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
        // box-shadow: 0 0 3px ${({ theme }) => theme.highlight};
    }

    ${({ disabled, isSubmitButton }) => disabled === false && isSubmitButton ? `
        animation: callToAction;
        animation-duration: 1s;
        animation-fill-mode: forwards;
    `: null}
`;

const Label = styled.div`
    display: flex;
    padding: 0.3em 0.5em;
`;

const rippleAnimation = keyframes`
    to {
        transform: scale(50);
        opacity: 0;
    }
`;

const Wave = styled.div`
    position: absolute;
    background-color: #ffffff66;
    border-radius: 50%;
    width: 10px;
    height: 10px;
    transform: scale(0);
    pointer-events: none;
    animation: ${rippleAnimation} 0.6s linear;
`;

export const IWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events:none;
`;

const L = styled(Link)`
    all: unset;
    display: flex;
    flex-direction: column;
    width: fit-content;
`
const BtnLink = styled.a`
    all: unset;
    width: 100%;
`

const LinkTo = ({ ...props }) => {
    const location = useLocation();
    const to = props.to;
    delete props.to;

    return (
        <L to={to}>
            <Button {...props} currentLoc={location.pathname === to} />
        </L>
    )
}

const Button = ({ ...props }) => {
    const buttonRef = useRef()
    const [wavePosition, setWavePosition] = useState({ x: null, y: null });

    const handleClick = (e) => {
        setWavePosition({ x: null, y: null });
        const rect = buttonRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setWavePosition({ x, y });
        setTimeout(() => setWavePosition({ x: null, y: null }), 600)
    };

    if (typeof props.children === 'string') {
        props.children = pascalCase(props.children)
    }

    const buttonText = props.label ? props.label : props.href ? <BtnLink href={props.href}>{props.children}</BtnLink> : props.children;

    const accountURL = buildAccountURL(props.toAccount, props.customerId, props.loyaltyAccountId);
    if (accountURL) {
        props.to = accountURL;
        delete props.toAccount;
        delete props.customerId;
        delete props.loyaltyAccountId;
    }

    return (props.to)
        ? <LinkTo {...props} />
        : (
            <RoleProtected roles={props.roles}>
                <Wrapper
                    type='button'
                    match={props.currentLoc}
                    onMouseDown={handleClick}
                    ref={buttonRef}
                    {...props}
                >
                    {props.icon ? <IWrapper><Icon icon={props.icon} /></IWrapper> : null}
                    {buttonText}
                    {wavePosition.x !== null && (
                        <Wave style={{ left: wavePosition.x, top: wavePosition.y }} />
                    )}
                </Wrapper>
            </RoleProtected>
        )
};

export default Button;

export const BorderLessButton = styled(Button)`
    border: unset;
    padding: 0.5em;
    transition: all 0.2s;
    border-radius: 0.5rem;
    background-color: unset;
    color: ${({ theme, color }) => theme[color] || 'inherit'};

    &:hover {
        background-color: ${({ theme }) => theme.button.hover}80;
    }

    &:focus {
        outline: none !important;
        border: unset;
    }
`